import React, { useEffect, useRef, useState } from 'react';
import {
  Box, VStack, HStack, SimpleGrid, Text, Badge, Button, Icon, Link, Divider,
  Tabs, TabList, TabPanels, Tab, TabPanel, Select, useToast, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, Tag,
} from '@chakra-ui/react';
import { FiFile, FiUpload, FiTrash2, FiMapPin } from 'react-icons/fi';
import {
  ImmExpediente, ImmArchivo, obtenerExpediente,
  subirArchivoExpediente, eliminarArchivoExpediente,
  listarAtenciones, listarCasos, listarDecisiones, listarOrdenes,
  listarCanalizaciones, listarCitas, listarConstancias,
  fileUrl, ImmAtencion, ImmCaso, ImmDecision, ImmOrden, ImmCanalizacion, ImmCita, ImmConstancia,
} from '../../../api/immApi';
import {
  SectionTitle, Field, fmt, fmtDateTime, nombreCompleto, IMM_COLOR, IMM_COLOR_HOVER,
  EstatusBadge, NIVEL_RIESGO_COLOR, TIPO_DECISION_LABEL, GaleriaEvidencias,
} from '../_shared';
import LineaTiempo from './LineaTiempo';

interface Props {
  expedienteId: number;
}

const TIPOS_DOC = ['INE', 'CURP', 'ComprobanteDomicilio', 'DocumentoCaso', 'Evidencia'];

const ExpedienteDetalle: React.FC<Props> = ({ expedienteId }) => {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [exp, setExp] = useState<ImmExpediente | null>(null);
  const [cargando, setCargando] = useState(true);
  const [tipoDoc, setTipoDoc] = useState('INE');
  const [subiendo, setSubiendo] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const cargar = async () => {
    setCargando(true);
    try {
      setExp(await obtenerExpediente(expedienteId));
    } catch {
      setExp(null);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [expedienteId]);

  const handleArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSubiendo(true);
    try {
      await subirArchivoExpediente(expedienteId, file, { tipo: tipoDoc });
      toast({ title: 'Documento subido', status: 'success', duration: 2500 });
      cargar();
    } catch (error: any) {
      toast({ title: 'Error al subir', description: error?.response?.data?.message, status: 'error', duration: 4000 });
    } finally {
      setSubiendo(false);
    }
  };

  const handleEliminarArchivo = async (a: ImmArchivo) => {
    try {
      await eliminarArchivoExpediente(expedienteId, a.id);
      toast({ title: 'Documento eliminado', status: 'success', duration: 2000 });
      cargar();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message, status: 'error', duration: 3000 });
    }
  };

  if (cargando) {
    return <HStack py={8} justify="center"><Spinner color={IMM_COLOR} /><Text color="gray.500">Cargando expediente...</Text></HStack>;
  }
  if (!exp) return <Text color="gray.400" py={8} textAlign="center">Expediente no disponible.</Text>;

  const domicilio = [
    exp.tipoVialidad, exp.vialidad,
    exp.numeroExterior ? `No. ${exp.numeroExterior}` : '',
    exp.numeroInterior ? `Int. ${exp.numeroInterior}` : '',
    exp.colonia, exp.localidad, exp.municipio, exp.estado, exp.codigoPostal ? `C.P. ${exp.codigoPostal}` : '',
  ].filter(Boolean).join(', ');

  return (
    <VStack align="stretch" spacing={3}>
      {/* Encabezado */}
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <Box>
          <Text fontSize="lg" fontWeight="bold" color={IMM_COLOR}>{nombreCompleto(exp)}</Text>
          <HStack spacing={2}>
            <Tag size="sm" colorScheme="red" variant="subtle">{exp.folio}</Tag>
            <EstatusBadge value={exp.estatus} />
            {exp.edad != null && <Text fontSize="xs" color="gray.500">{exp.edad} años</Text>}
          </HStack>
        </Box>
      </HStack>

      <Tabs colorScheme="red" variant="enclosed" size="sm" isLazy>
        <TabList flexWrap="wrap">
          <Tab>Resumen</Tab>
          <Tab>Documentos</Tab>
          <Tab>Atenciones</Tab>
          <Tab>Casos</Tab>
          <Tab>Decisiones</Tab>
          <Tab>Órdenes</Tab>
          <Tab>Canalizaciones</Tab>
          <Tab>Citas</Tab>
          <Tab>Constancias</Tab>
        </TabList>

        <TabPanels>
          {/* Resumen + línea de tiempo */}
          <TabPanel px={0}>
            <SectionTitle>Datos personales</SectionTitle>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <Field label="NOMBRE COMPLETO" value={nombreCompleto(exp)} />
              <Field label="EDAD" value={exp.edad != null ? `${exp.edad} años` : undefined} />
              <Field label="FECHA DE NACIMIENTO" value={fmt(exp.fechaNacimiento)} />
              <Field label="CURP" value={exp.curp} />
              <Field label="ESTADO CIVIL" value={exp.estadoCivil} />
              <Field label="ESCOLARIDAD" value={exp.escolaridad} />
              <Field label="OCUPACIÓN" value={exp.ocupacion} />
              <Field label="TELÉFONO" value={exp.telefono} />
              <Field label="CELULAR" value={exp.celular} />
              <Field label="CORREO" value={exp.email} />
            </SimpleGrid>

            <SectionTitle>Domicilio</SectionTitle>
            <Field label="DIRECCIÓN" value={domicilio || undefined} />
            {exp.referencias && <Box mt={2}><Field label="REFERENCIAS" value={exp.referencias} /></Box>}
            {exp.urlMaps && (
              <Link href={exp.urlMaps} isExternal color="blue.500" fontSize="sm" mt={2} display="inline-flex" alignItems="center">
                <Icon as={FiMapPin} mr={1} /> Ver ubicación en mapa
              </Link>
            )}

            <SectionTitle>Información adicional</SectionTitle>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <Field label="NÚMERO DE HIJAS/HIJOS" value={exp.numeroHijos != null ? String(exp.numeroHijos) : undefined} />
              <Field label="CONTACTO DE EMERGENCIA" value={exp.contactoEmergenciaNombre} />
              <Field label="TEL. EMERGENCIA" value={exp.contactoEmergenciaTelefono} />
            </SimpleGrid>
            {exp.observaciones && <Box mt={2}><Field label="OBSERVACIONES" value={exp.observaciones} /></Box>}

            <SectionTitle>Línea de tiempo</SectionTitle>
            <LineaTiempo expedienteId={expedienteId} refreshKey={refreshKey} />
          </TabPanel>

          {/* Documentos */}
          <TabPanel px={0}>
            <HStack mb={3} spacing={2}>
              <Select size="sm" w="220px" value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value)}>
                {TIPOS_DOC.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleArchivo} />
              <Button size="sm" leftIcon={<Icon as={FiUpload} />} colorScheme="red" variant="outline"
                onClick={() => fileRef.current?.click()} isLoading={subiendo}>
                Subir documento
              </Button>
            </HStack>
            {(exp.archivos || []).length === 0 ? (
              <Text color="gray.400" fontSize="sm" py={4}>Sin documentos cargados (INE, CURP, comprobante de domicilio, etc.).</Text>
            ) : (
              <GaleriaEvidencias archivos={exp.archivos} onEliminar={handleEliminarArchivo} />
            )}
          </TabPanel>

          {/* Sub-entidades relacionadas (solo lectura) */}
          <TabPanel px={0}>
            <RelacionAtenciones expedienteId={expedienteId} />
          </TabPanel>
          <TabPanel px={0}>
            <RelacionCasos expedienteId={expedienteId} />
          </TabPanel>
          <TabPanel px={0}>
            <RelacionDecisiones expedienteId={expedienteId} />
          </TabPanel>
          <TabPanel px={0}>
            <RelacionOrdenes expedienteId={expedienteId} />
          </TabPanel>
          <TabPanel px={0}>
            <RelacionCanalizaciones expedienteId={expedienteId} />
          </TabPanel>
          <TabPanel px={0}>
            <RelacionCitas expedienteId={expedienteId} />
          </TabPanel>
          <TabPanel px={0}>
            <RelacionConstancias expedienteId={expedienteId} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Text fontSize="xs" color="gray.400">
        Para registrar nuevos eventos usa los submódulos correspondientes (Atenciones, Casos, Decisiones, etc.), seleccionando este expediente.
      </Text>
    </VStack>
  );
};

// ---- Sub-tablas de relaciones (solo lectura, cargadas por expedienteId) ----

function useRelacion<T>(fn: (p: any) => Promise<T[]>, expedienteId: number) {
  const [rows, setRows] = useState<T[]>([]);
  const [cargando, setCargando] = useState(true);
  useEffect(() => {
    let vivo = true;
    setCargando(true);
    fn({ expedienteId })
      .then((d) => { if (vivo) setRows(d || []); })
      .catch(() => { if (vivo) setRows([]); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [fn, expedienteId]);
  return { rows, cargando };
}

const Vacio = ({ texto }: { texto: string }) => <Text color="gray.400" fontSize="sm" py={4}>{texto}</Text>;
const Cargando = () => <HStack py={4}><Spinner size="sm" color={IMM_COLOR} /><Text fontSize="sm" color="gray.500">Cargando...</Text></HStack>;

const RelacionAtenciones = ({ expedienteId }: { expedienteId: number }) => {
  const { rows, cargando } = useRelacion<ImmAtencion>(listarAtenciones, expedienteId);
  if (cargando) return <Cargando />;
  if (!rows.length) return <Vacio texto="Sin atenciones registradas." />;
  return (
    <Box overflowX="auto"><Table size="sm" variant="simple">
      <Thead bg={IMM_COLOR}><Tr>
        <Th color="white" fontSize="xs">Fecha</Th><Th color="white" fontSize="xs">Medio</Th>
        <Th color="white" fontSize="xs">Motivo</Th><Th color="white" fontSize="xs">Estatus</Th>
      </Tr></Thead>
      <Tbody>{rows.map((a) => (
        <Tr key={a.id}><Td>{fmt(a.fecha)}</Td><Td>{a.medioLlegada}</Td><Td>{a.motivo || '—'}</Td><Td><EstatusBadge value={a.estatus} /></Td></Tr>
      ))}</Tbody>
    </Table></Box>
  );
};

const RelacionCasos = ({ expedienteId }: { expedienteId: number }) => {
  const { rows, cargando } = useRelacion<ImmCaso>(listarCasos, expedienteId);
  if (cargando) return <Cargando />;
  if (!rows.length) return <Vacio texto="Sin casos registrados." />;
  return (
    <Box overflowX="auto"><Table size="sm" variant="simple">
      <Thead bg={IMM_COLOR}><Tr>
        <Th color="white" fontSize="xs">Fecha incidente</Th><Th color="white" fontSize="xs">Tipo violencia</Th>
        <Th color="white" fontSize="xs">Riesgo</Th><Th color="white" fontSize="xs">Estatus</Th>
      </Tr></Thead>
      <Tbody>{rows.map((c) => (
        <Tr key={c.id}>
          <Td>{fmt(c.fechaIncidente)}</Td><Td>{c.tipoViolencia?.descripcion || '—'}</Td>
          <Td><Badge colorScheme={NIVEL_RIESGO_COLOR[c.nivelRiesgo] || 'gray'}>{c.nivelRiesgo}</Badge></Td>
          <Td><EstatusBadge value={c.estatus} /></Td>
        </Tr>
      ))}</Tbody>
    </Table></Box>
  );
};

const RelacionDecisiones = ({ expedienteId }: { expedienteId: number }) => {
  const { rows, cargando } = useRelacion<ImmDecision>(listarDecisiones, expedienteId);
  if (cargando) return <Cargando />;
  if (!rows.length) return <Vacio texto="Sin decisiones registradas." />;
  return (
    <Box overflowX="auto"><Table size="sm" variant="simple">
      <Thead bg={IMM_COLOR}><Tr>
        <Th color="white" fontSize="xs">Fecha</Th><Th color="white" fontSize="xs">Decisión</Th><Th color="white" fontSize="xs">Observaciones</Th>
      </Tr></Thead>
      <Tbody>{rows.map((d) => (
        <Tr key={d.id}><Td>{fmtDateTime(d.fecha)}</Td><Td>{TIPO_DECISION_LABEL[d.tipoDecision] || d.tipoDecision}</Td><Td>{d.observaciones || '—'}</Td></Tr>
      ))}</Tbody>
    </Table></Box>
  );
};

const RelacionOrdenes = ({ expedienteId }: { expedienteId: number }) => {
  const { rows, cargando } = useRelacion<ImmOrden>(listarOrdenes, expedienteId);
  if (cargando) return <Cargando />;
  if (!rows.length) return <Vacio texto="Sin órdenes de protección." />;
  return (
    <Box overflowX="auto"><Table size="sm" variant="simple">
      <Thead bg={IMM_COLOR}><Tr>
        <Th color="white" fontSize="xs">No. expediente</Th><Th color="white" fontSize="xs">Autoridad</Th>
        <Th color="white" fontSize="xs">Emisión</Th><Th color="white" fontSize="xs">Estatus</Th>
      </Tr></Thead>
      <Tbody>{rows.map((o) => (
        <Tr key={o.id}><Td>{o.numeroExpediente || '—'}</Td><Td>{o.autoridadEmisora || '—'}</Td><Td>{fmt(o.fechaEmision)}</Td><Td><EstatusBadge value={o.estatus} /></Td></Tr>
      ))}</Tbody>
    </Table></Box>
  );
};

const RelacionCanalizaciones = ({ expedienteId }: { expedienteId: number }) => {
  const { rows, cargando } = useRelacion<ImmCanalizacion>(listarCanalizaciones, expedienteId);
  if (cargando) return <Cargando />;
  if (!rows.length) return <Vacio texto="Sin canalizaciones." />;
  return (
    <Box overflowX="auto"><Table size="sm" variant="simple">
      <Thead bg={IMM_COLOR}><Tr>
        <Th color="white" fontSize="xs">Folio</Th><Th color="white" fontSize="xs">Fecha</Th>
        <Th color="white" fontSize="xs">Dependencia destino</Th><Th color="white" fontSize="xs">Estatus</Th>
      </Tr></Thead>
      <Tbody>{rows.map((c) => (
        <Tr key={c.id}><Td>{c.folio}</Td><Td>{fmt(c.fecha)}</Td><Td>{c.dependenciaDestino || '—'}</Td><Td><EstatusBadge value={c.estatus} /></Td></Tr>
      ))}</Tbody>
    </Table></Box>
  );
};

const RelacionCitas = ({ expedienteId }: { expedienteId: number }) => {
  const { rows, cargando } = useRelacion<ImmCita>(listarCitas, expedienteId);
  if (cargando) return <Cargando />;
  if (!rows.length) return <Vacio texto="Sin citas psicológicas." />;
  return (
    <Box overflowX="auto"><Table size="sm" variant="simple">
      <Thead bg={IMM_COLOR}><Tr>
        <Th color="white" fontSize="xs">Fecha</Th><Th color="white" fontSize="xs">Horario</Th>
        <Th color="white" fontSize="xs">Psicólogo/a</Th><Th color="white" fontSize="xs">Estatus</Th>
      </Tr></Thead>
      <Tbody>{rows.map((c) => (
        <Tr key={c.id}><Td>{fmt(c.fecha)}</Td><Td>{c.horaInicio || '—'}{c.horaFin ? ` - ${c.horaFin}` : ''}</Td><Td>{c.psicologo || '—'}</Td><Td><EstatusBadge value={c.estatus} /></Td></Tr>
      ))}</Tbody>
    </Table></Box>
  );
};

const RelacionConstancias = ({ expedienteId }: { expedienteId: number }) => {
  const { rows, cargando } = useRelacion<ImmConstancia>(listarConstancias, expedienteId);
  if (cargando) return <Cargando />;
  if (!rows.length) return <Vacio texto="Sin constancias emitidas." />;
  return (
    <Box overflowX="auto"><Table size="sm" variant="simple">
      <Thead bg={IMM_COLOR}><Tr>
        <Th color="white" fontSize="xs">Folio</Th><Th color="white" fontSize="xs">Tipo</Th>
        <Th color="white" fontSize="xs">Fecha</Th><Th color="white" fontSize="xs">Estatus</Th>
      </Tr></Thead>
      <Tbody>{rows.map((c) => (
        <Tr key={c.id}><Td>{c.folio}</Td><Td>{c.tipoDocumento || '—'}</Td><Td>{fmt(c.fecha)}</Td><Td><EstatusBadge value={c.estatus} /></Td></Tr>
      ))}</Tbody>
    </Table></Box>
  );
};

export default ExpedienteDetalle;
